import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, OnDestroy, effect } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, startWith, switchMap, takeUntil } from 'rxjs';

import { getControlErrorMessage$ } from '../../utils/functions';
import { Language } from '../../../core/services/language';

@Component({
  selector: 'app-input-field',
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './input-field.html',
  styleUrl: './input-field.css',
})

export class InputField implements OnInit, OnDestroy {
  @Input() label!: string;
  @Input() placeholder!: string;
  @Input() control: FormControl<any> | null = null;
  @Input() type = 'text';
  @Input() customClass = '';
  @Input() hasError = false;
  @Input() errorMessage: string | null = null;
  @Input() showInlineError = true;
  @Input() isMobile?: boolean = false;
  @Input() showWarningIcon?: boolean = false;
  @Input() isRequired?: boolean = true;
  @Input() forceLang?: 'ar' | 'en' | null = null;

  lang = localStorage.getItem('app-lang') as 'en' | 'ar';
  computedErrorMessage: string | null = null;
  dir: string = this.lang === 'en' ? 'ltr' : 'rtl';
  private translate = inject(TranslateService);
  private languageService = inject(Language);
  private destroy$ = new Subject<void>();


  constructor() {
    effect(() => {
      const currentLang = this.languageService.language();

      this.dir = currentLang === 'en' ? 'ltr' : 'rtl';
      this.lang = this.forceLang ? this.forceLang : currentLang;
    });

  }

  ngOnInit() {

    if (this.control) {
      this.control.statusChanges
        .pipe(
          startWith(this.control.status),
          switchMap(() => getControlErrorMessage$(this.control, this.translate)),
          takeUntil(this.destroy$)
        )
        .subscribe((msg) => {
          this.computedErrorMessage =
            this.control?.dirty && this.control.invalid ? msg : null;
        });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showError(): boolean {
    return (
      (this.showInlineError ?? true) &&
      (this.hasError || !!this.computedErrorMessage)
    );
  }

  private readonly _autoId =
    `input-${Math.random().toString(36).slice(2, 9)}`;

  get inputId(): string {
    return this._autoId;
  }
}
